import { computed, onBeforeMount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router'; // Import useRoute for router data

import { useDetailNavigation } from '@/components/details/hooks/useDetailNavigation';
import { useLoadingStore } from '@/stores/loading';
import { useSplitViewStore } from '@/stores/splitview';
interface UseDetailViewOptions {
  fetchData: () => Promise<void>;
  fetchChangeRecords?: () => Promise<void>;
  fetchLookups?: () => Promise<void>;
  clearValues?: () => void;
  isSubComponent?: boolean;
}

export function useDetailView({
  fetchData,
  fetchChangeRecords,
  fetchLookups,
  clearValues,
  isSubComponent,
}: UseDetailViewOptions) {
  const componentKey = ref(0); // Key for re-rendering the component
  const loadingStore = useLoadingStore();
  const { id, isNew, editMode } = useDetailNavigation({ isSubComponent });

  const route = useRoute(); // Access on current route
  const splitViewStore = useSplitViewStore();

  const routerViewName = computed(() => {
    const { isSplitView, subComponentMode, subComponentID } = route.params;
    if (!splitViewStore.isSplitView || !isSplitView) {
      return 'main';
    } else if (splitViewStore.isSplitView && isSplitView && (subComponentMode || subComponentID)) {
      return 'modal';
    }
    return 'split';
  });

  function refreshComponent() {
    componentKey.value += 1;
  }

  function clearComponent() {
    clearValues?.();
  }

  watch(
    [id, isNew, editMode],
    async ([newId, newIsNew, newEditMode], [oldId, oldIsNew, oldEditMode]) => {
      if (newIsNew !== oldIsNew) {
        if (newIsNew) {
          clearComponent();
          refreshComponent();
        }
      }

      if (newEditMode !== oldEditMode) {
        refreshComponent();
      }

      if (newId !== oldId && !newIsNew) {
        loadingStore.startLoading();
        try {
          await fetchData();
          if (fetchChangeRecords) await fetchChangeRecords();
          if (fetchLookups) await fetchLookups();
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          refreshComponent();
          loadingStore.stopLoading();
        }
      }
    },
  );

  onBeforeMount(() => {
    loadingStore.startLoading();
  });

  onMounted(async () => {
    try {
      if (!isNew.value) {
        await fetchData();
        if (fetchChangeRecords) await fetchChangeRecords();
      }
      if (fetchLookups) await fetchLookups();
    } catch (error) {
      console.error('Error in onMounted:', error);
    } finally {
      loadingStore.stopLoading();
    }
  });

  return { componentKey, routerViewName };
}
